import React, { useMemo, useState } from 'react';
import { BarChart3, X, PieChart } from 'lucide-react';

const SpinStatistics = ({ results = [], entries = [] }) => {
  const [showModal, setShowModal] = useState(false);

  // Calculate statistics
  const stats = useMemo(() => {
    if (results.length === 0) return null;

    // Count wins per entry
    const winCounts = {};
    results.forEach(result => {
      const key = result.text;
      winCounts[key] = (winCounts[key] || 0) + 1;
    });

    // Sort by count descending
    const sorted = Object.entries(winCounts)
      .map(([name, count]) => ({
        name,
        count,
        percentage: ((count / results.length) * 100).toFixed(1),
        color: results.find(r => r.text === name)?.color || '#ccc'
      }))
      .sort((a, b) => b.count - a.count);

    return {
      totalSpins: results.length,
      uniqueWinners: sorted.length,
      distribution: sorted,
      mostFrequent: sorted[0],
      leastFrequent: sorted[sorted.length - 1]
    };
  }, [results]);

  if (results.length === 0) {
    return null;
  }

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="btn-action-bar"
        aria-label="Statistics"
        title="View spin statistics"
        data-testid="statistics-button"
      >
        <BarChart3 size={20} />
        <span>Stats</span>
      </button>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal stats-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>
              <X size={20} />
            </button>
            
            <h2>📊 Spin Statistics</h2>

            {stats && (
              <>
                {/* Summary cards */}
                <div className="stats-summary">
                  <div className="stat-card">
                    <span className="stat-value">{stats.totalSpins}</span>
                    <span className="stat-label">Total Spins</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-value">{stats.uniqueWinners}</span>
                    <span className="stat-label">Unique Winners</span>
                  </div>
                  <div className="stat-card highlight">
                    <span className="stat-value">{stats.mostFrequent?.name}</span>
                    <span className="stat-label">Most Wins ({stats.mostFrequent?.count})</span>
                  </div>
                </div>

                {/* Visual pie chart */}
                <div className="stats-chart">
                  <div className="pie-chart">
                    {stats.distribution.map((item, index) => {
                      const rotation = stats.distribution
                        .slice(0, index)
                        .reduce((sum, i) => sum + (i.count / stats.totalSpins) * 360, 0);
                      const angle = (item.count / stats.totalSpins) * 360;
                      
                      return (
                        <div
                          key={item.name}
                          className="pie-slice"
                          style={{
                            '--rotation': `${rotation}deg`,
                            '--angle': `${angle}deg`,
                            '--color': item.color
                          }}
                          title={`${item.name}: ${item.count} wins (${item.percentage}%)`}
                        />
                      );
                    })}
                    <div className="pie-center">
                      <PieChart size={24} />
                    </div>
                  </div>
                </div>

                {/* Distribution list */}
                <div className="stats-distribution">
                  <h3>Win Distribution</h3>
                  <ul className="distribution-list">
                    {stats.distribution.map((item) => (
                      <li key={item.name} className="distribution-item">
                        <span 
                          className="dist-color" 
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="dist-name">{item.name}</span>
                        <div className="dist-bar-container">
                          <div 
                            className="dist-bar" 
                            style={{ 
                              width: `${item.percentage}%`,
                              backgroundColor: item.color 
                            }}
                          />
                        </div>
                        <span className="dist-count">{item.count}</span>
                        <span className="dist-percent">{item.percentage}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SpinStatistics;
